import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { bookingsAPI } from '../services/api';
import { useLocation } from 'react-router-dom';

const getLocalDateString = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStatusColor = (status) => {
  const colors = {
    confirmed: 'success',
    pending: 'warning',
    cancelled: 'error',
    completed: 'info',
  };
  return colors[status] || 'default';
};

// Whether a given event (from the events list, or a booking's own event_key/name)
// is a birthday package - used to decide when to show the child name/gender fields.
const isBirthdayEventEntry = (event) => {
  if (!event) return false;
  return (
    event.display_location === 'birthday' ||
    event.event_key?.toLowerCase().includes('birthday') ||
    event.event_key?.toLowerCase().includes('geburtstag') ||
    event.name?.toLowerCase().includes('geburtstag')
  );
};

const genderLabel = (gender) => {
  if (gender === 'junge') return 'Junge';
  if (gender === 'maedchen') return 'Mädchen';
  return '';
};

const Bookings = ({ user }) => {
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0=Today, 1=Future, 2=Past
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventConfig, setEventConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState({});
  const [selectedExtras, setSelectedExtras] = useState({});
  const [selectedExtraTimeSlots, setSelectedExtraTimeSlots] = useState({});
  const [trampolineEventTimeSlots, setTrampolineEventTimeSlots] = useState([]);
  const [loadingTrampolineSlots, setLoadingTrampolineSlots] = useState(false);

  useEffect(() => {
    loadBookings();
    loadEvents();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      // Request all bookings by setting a high limit
      const response = await bookingsAPI.list({ limit: 10000 });
      if (response.data.success) {
        setBookings(response.data.data.bookings);
      }
    } catch (err) {
      setError('Fehler beim Laden der Buchungen');
    } finally {
      setLoading(false);
    }
  };

  // Check if we need to open a specific booking from notification
  useEffect(() => {
    if (location.state?.openBookingId && bookings.length > 0) {
      const booking = bookings.find(b => b.booking_id === location.state.openBookingId);
      if (booking) {
        handleViewDetails(booking);
        // Clear the state so it doesn't reopen on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, bookings]);

  const loadEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://api.goldfroeschli-kinderwelt.ch/api/events/list.php', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (err) {
    }
  };

  const handleViewDetails = async (booking) => {
    try {
      // Fetch full booking details including children and extras from relational tables
      const token = localStorage.getItem('token');
      const response = await bookingsAPI.getDetails(booking.booking_id, token);
      
      if (response.data.success) {
        setSelectedBooking({
          ...booking,
          childrenDetails: response.data.data.children,
          extrasDetails: response.data.data.extras
        });
      } else {
        // Fallback to basic booking data
        setSelectedBooking(booking);
      }
    } catch (err) {
      // Fallback to basic booking data
      setSelectedBooking(booking);
    }
    setDetailsOpen(true);
  };

  const handleDownloadTicket = async (bookingId) => {
    try {
      const response = await bookingsAPI.getTicket(bookingId);
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ticket_${bookingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Fehler beim Herunterladen des Tickets. Möglicherweise wurde das PDF noch nicht generiert.');
    }
  };

  const handleViewTicket = async (bookingId) => {
    try {
      const response = await bookingsAPI.getTicket(bookingId);
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      alert('Fehler beim Anzeigen des Tickets. Möglicherweise wurde das PDF noch nicht generiert.');
    }
  };

  const handleEditClick = (booking) => {
    setEditFormData({
      id: booking.id,
      booking_id: booking.booking_id,
      event_type_id: booking.event_type_id,
      status: booking.status,
      booking_date: booking.booking_date,
      time_slot: booking.time_slot,
      first_name: booking.first_name,
      last_name: booking.last_name,
      email: booking.email,
      phone: booking.phone,
      birthday_child_name: booking.birthday_child_name || '',
      birthday_child_gender: booking.birthday_child_gender || '',
      notes: booking.notes || '',
    });
    // Load event config with available time slots for this event and date
    if (booking.event_type_id && booking.booking_date) {
      loadEventConfig(booking.event_type_id, booking.booking_date, booking.time_slot);
    }
    setEditDialogOpen(true);
  };

  const handleCreateClick = () => {
    setEditFormData({
      event_type_id: '',
      status: 'confirmed',
      booking_date: getLocalDateString(),
      time_slot: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      birthday_child_name: '',
      birthday_child_gender: '',
      notes: '',
    });
    setSelectedChildren({});
    setSelectedExtras({});
    setSelectedExtraTimeSlots({});
    setEventConfig(null);
    setTrampolineEventTimeSlots([]);
    setCreateDialogOpen(true);
  };

  const loadEventConfig = async (eventId, date = null, currentTimeSlot = null) => {
    if (!eventId) return;
    
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem('token');
      
      // Load event config
      const configResponse = await fetch(
        `https://api.goldfroeschli-kinderwelt.ch/api/events/get_config.php?event_id=${eventId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const configData = await configResponse.json();
      
      if (configData.success) {
        // Load time slots if date is selected
        let timeSlots = configData.data.timeSlots || [];
        
        if (date) {
          const slotsResponse = await fetch(
            `https://api.goldfroeschli-kinderwelt.ch/api/events/get_available_slots.php?event_id=${eventId}&date=${date}`,
            { headers: { Authorization: `Bearer ${token}` }}
          );
          const slotsData = await slotsResponse.json();
          if (slotsData.success) {
            timeSlots = slotsData.data;
          }
        }
        
        setEventConfig({
          childCategories: configData.data.childCategories || [],
          extras: configData.data.extras || [],
          timeSlots: timeSlots
        });
        
        // If current time slot is not in the available slots, clear it
        if (currentTimeSlot && timeSlots.length > 0) {
          const slotExists = timeSlots.some(slot => slot.time_slot === currentTimeSlot);
          if (!slotExists && editFormData.event_type_id === eventId) {
            setEditFormData({ ...editFormData, time_slot: '' });
          }
        }
      }
      
      // Load Trampoline event time slots if date is selected
      if (date) {
        loadTrampolineEventTimeSlots(date);
      }
    } catch (err) {
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadTrampolineEventTimeSlots = async (date) => {
    try {
      setLoadingTrampolineSlots(true);
      const token = localStorage.getItem('token');
      
      const slotsResponse = await fetch(
        `https://api.goldfroeschli-kinderwelt.ch/api/events/get_available_slots.php?event_id=4&date=${date}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const slotsData = await slotsResponse.json();
      
      if (slotsData.success && slotsData.data) {
        const trampolineSlots = Array.isArray(slotsData.data) ? slotsData.data : [];
        setTrampolineEventTimeSlots(trampolineSlots);
      }
    } catch (err) {
      setTrampolineEventTimeSlots([]);
    } finally {
      setLoadingTrampolineSlots(false);
    }
  };

  const calculateDynamicPrice = () => {
    let total = 0;
    
    // Calculate based on child categories
    if (eventConfig?.childCategories) {
      eventConfig.childCategories.forEach(category => {
        const count = selectedChildren[category.id] || 0;
        total += count * parseFloat(category.price);
      });
    }
    
    // Calculate extras
    if (eventConfig?.extras) {
      eventConfig.extras.forEach(extra => {
        const count = selectedExtras[extra.id] || 0;
        const totalChildren = Object.values(selectedChildren).reduce((sum, c) => sum + c, 0);
        
        if (extra.price_type === 'per_child') {
          total += count * parseFloat(extra.price_per_child) * totalChildren;
        } else {
          total += count * parseFloat(extra.price_per_child);
        }
      });
    }
    
    return total.toFixed(2);
  };

  const handleSaveEdit = async () => {
    try {
      // Validate: Check if booking date is being updated to today after 18:00
      if (editFormData.booking_date) {
        const selectedDate = new Date(editFormData.booking_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        
        if (selectedDate.getTime() === today.getTime()) {
          const currentHour = new Date().getHours();
          if (currentHour >= 18) {
            setError('Buchungen für heute sind nach 18:00 Uhr nicht mehr möglich. Bitte wählen Sie ein zukünftiges Datum.');
            return;
          }
        }
      }

      if (
        isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) &&
        (!editFormData.birthday_child_name?.trim() || !editFormData.birthday_child_gender)
      ) {
        setError('Bitte Name und Geschlecht des Geburtstagskindes angeben.');
        return;
      }

      setSaving(true);
      const response = await bookingsAPI.update(editFormData);
      if (response.data.success) {
        setEditDialogOpen(false);
        loadBookings();
        setError('');
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren');
      }
    } catch (err) {
      setError('Fehler beim Aktualisieren der Buchung');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBooking = async () => {
    try {
      // Validate: Check if booking is for today after 18:00
      if (editFormData.booking_date) {
        const selectedDate = new Date(editFormData.booking_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        
        if (selectedDate.getTime() === today.getTime()) {
          const currentHour = new Date().getHours();
          if (currentHour >= 18) {
            setError('Buchungen für heute sind nach 18:00 Uhr nicht mehr möglich. Bitte wählen Sie ein zukünftiges Datum.');
            return;
          }
        }
      }

      if (
        isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) &&
        (!editFormData.birthday_child_name?.trim() || !editFormData.birthday_child_gender)
      ) {
        setError('Bitte Name und Geschlecht des Geburtstagskindes angeben.');
        return;
      }

      setSaving(true);
      
      // Calculate legacy fields for backward compatibility
      let children0to4 = 0;
      let children5to8 = 0;
      
      if (eventConfig?.childCategories) {
        eventConfig.childCategories.forEach(category => {
          const count = selectedChildren[category.id] || 0;
          if (category.age_max <= 4) {
            children0to4 += count;
          } else {
            children5to8 += count;
          }
        });
      }
      
      // Prepare children data
      const childrenObj = {};
      Object.keys(selectedChildren).forEach(categoryId => {
        if (selectedChildren[categoryId] > 0) {
          childrenObj[categoryId] = selectedChildren[categoryId];
        }
      });
      
      // Prepare extras data with quantities
      const extrasObj = {};
      Object.keys(selectedExtras).forEach(extraId => {
        if (selectedExtras[extraId] > 0) {
          extrasObj[extraId] = selectedExtras[extraId];
        }
      });
      
      const bookingData = {
        ...editFormData,
        children_0_4: children0to4,
        children_5_8: children5to8,
        children: childrenObj,
        total_price: calculateDynamicPrice(),
        extras: extrasObj,
        extraTimeSlots: selectedExtraTimeSlots
      };

      const response = await bookingsAPI.create(bookingData);
      if (response.data.success) {
        setCreateDialogOpen(false);
        setSelectedChildren({});
        setSelectedExtras({});
        setSelectedExtraTimeSlots({});
        setEventConfig(null);
        setTrampolineEventTimeSlots([]);
        loadBookings();
        setError('');
      } else {
        setError(response.data.message || 'Fehler beim Erstellen');
      }
    } catch (err) {
      setError('Fehler beim Erstellen der Buchung');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (booking) => {
    setSelectedBooking(booking);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      const response = await bookingsAPI.delete(selectedBooking.booking_id);
      if (response.data.success) {
        setDeleteDialogOpen(false);
        loadBookings();
        setError('');
      } else {
        setError(response.data.message || 'Fehler beim Löschen');
      }
    } catch (err) {
      setError('Fehler beim Löschen der Buchung');
    } finally {
      setSaving(false);
    }
  };

  // Filter bookings by search
  const searchFilteredBookings = bookings.filter(booking => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      booking.booking_id?.toLowerCase().includes(searchLower) ||
      booking.first_name?.toLowerCase().includes(searchLower) ||
      booking.last_name?.toLowerCase().includes(searchLower) ||
      booking.email?.toLowerCase().includes(searchLower) ||
      booking.phone?.toLowerCase().includes(searchLower) ||
      booking.event_name?.toLowerCase().includes(searchLower)
    );
  });

  // Filter bookings by date category
  const today = getLocalDateString();
  
  const todayBookings = searchFilteredBookings.filter(booking => booking.booking_date === today);
  const futureBookings = searchFilteredBookings.filter(booking => booking.booking_date > today);
  const pastBookings = searchFilteredBookings.filter(booking => booking.booking_date < today);

  // Get current tab bookings
  const getCurrentTabBookings = () => {
    switch (tabValue) {
      case 0: return todayBookings;
      case 1: return futureBookings;
      case 2: return pastBookings;
      default: return searchFilteredBookings;
    }
  };

  const columns = [
    {
      field: 'booking_id',
      headerName: 'Buchungs-ID',
      width: 150,
      fontWeight: 600,
    },
    {
      field: 'customer',
      headerName: 'Kunde',
      width: 200,
      valueGetter: (params) => `${params.row.first_name} ${params.row.last_name}`,
    },
    {
      field: 'event_name',
      headerName: 'Event',
      width: 150,
    },
    {
      field: 'birthday_child_name',
      headerName: 'Geburtstagskind',
      width: 170,
      valueGetter: (params) => {
        const name = params.row.birthday_child_name;
        const gender = genderLabel(params.row.birthday_child_gender);
        if (!name && !gender) return '';
        return `${name || '—'}${gender ? ` (${gender})` : ''}`;
      },
    },
    {
      field: 'booking_date',
      headerName: 'Datum',
      width: 120,
    },
    {
      field: 'time_slot',
      headerName: 'Zeit',
      width: 100,
    },
    {
      field: 'total_price',
      headerName: 'Preis',
      width: 120,
      valueFormatter: (params) => `CHF ${parseFloat(params.value).toFixed(2)}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip label={params.value} color={getStatusColor(params.value)} size="small" />
      ),
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: user?.role === 'admin' ? 150 : (user?.role === 'manager' ? 120 : 100),
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            color="primary"
            onClick={() => handleViewDetails(params.row)}
            size="small"
            title="Ansehen"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="info"
            onClick={() => handleEditClick(params.row)}
            size="small"
            title="Bearbeiten"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {user?.role === 'admin' && (
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              size="small"
              title="Löschen"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          Buchungen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{ px: 3 }}
        >
          Neue Buchung
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            label="Suchen"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buchungs-ID, Name, E-Mail, Telefon..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={
              <Badge badgeContent={todayBookings.length} color="primary">
                <Box sx={{ pr: 2 }}>Heute</Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={futureBookings.length} color="info">
                <Box sx={{ pr: 2 }}>Zukünftig</Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={pastBookings.length} color="default">
                <Box sx={{ pr: 2 }}>Vergangen</Box>
              </Badge>
            } 
          />
        </Tabs>
        
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={getCurrentTabBookings()}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100, 200]}
              loading={loading}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Booking Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Buchungsdetails
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Grid container spacing={3}>
              {/* Booking Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  BUCHUNGSINFORMATION
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Buchungs-ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedBooking.booking_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={selectedBooking.status} 
                          color={getStatusColor(selectedBooking.status)} 
                          size="small" 
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Erstellt am
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedBooking.created_at).toLocaleString('de-DE')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Event
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedBooking.event_name}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Customer Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  KUNDENDATEN
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedBooking.first_name} {selectedBooking.last_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        E-Mail
                      </Typography>
                      <Typography variant="body1">
                        {selectedBooking.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Telefon
                      </Typography>
                      <Typography variant="body1">
                        {selectedBooking.phone}
                      </Typography>
                    </Grid>
                    {(selectedBooking.birthday_child_name || selectedBooking.birthday_child_gender) && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Geburtstagskind
                        </Typography>
                        <Typography variant="body1">
                          {selectedBooking.birthday_child_name || '—'}
                          {selectedBooking.birthday_child_gender === 'junge' && ' (Junge)'}
                          {selectedBooking.birthday_child_gender === 'maedchen' && ' (Mädchen)'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Event Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  EVENT DETAILS
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Datum
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedBooking.booking_date}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Uhrzeit
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedBooking.time_slot}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Children Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  KINDER
                </Typography>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  {selectedBooking.childrenDetails && selectedBooking.childrenDetails.length > 0 ? (
                    <Grid container spacing={2}>
                      {selectedBooking.childrenDetails.map((child, index) => (
                        <Grid item xs={6} key={index}>
                          <Typography variant="caption" color="text.secondary">
                            {child.category_name}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {child.quantity} Kinder (CHF {parseFloat(child.price).toFixed(2)} / Kind)
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    // Fallback to old fields
                    <Grid container spacing={2}>
                      {selectedBooking.children_0_4 > 0 && (
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Kinder (0-4 Jahre)
                          </Typography>
                          <Typography variant="body1">
                            {selectedBooking.children_0_4 || 0}
                          </Typography>
                        </Grid>
                      )}
                      {selectedBooking.children_5_8 > 0 && (
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Kinder (5-8 Jahre)
                          </Typography>
                          <Typography variant="body1">
                            {selectedBooking.children_5_8 || 0}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              </Grid>

              {/* Extras */}
              {((selectedBooking.extrasDetails && selectedBooking.extrasDetails.length > 0) || 
                selectedBooking.extra_trampoline || selectedBooking.extra_attractions) && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      EXTRAS
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      {selectedBooking.extrasDetails && selectedBooking.extrasDetails.length > 0 ? (
                        // Show new relational extras
                        selectedBooking.extrasDetails.map((extra, index) => (
                          <Box key={index} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip 
                                label={`${extra.extra_name} (${extra.quantity}x)`}
                                size="small"
                              />
                            </Box>
                            {extra.time_slot && (
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                                ⏰ Uhrzeit: {extra.time_slot}
                              </Typography>
                            )}
                          </Box>
                        ))
                      ) : (
                        // Fallback to old extras
                        <>
                          {selectedBooking.extra_trampoline && (
                            <Chip label="Extra Trampolin" sx={{ mr: 1, mb: 1 }} />
                          )}
                          {selectedBooking.extra_attractions && (
                            <Chip label="Alle Attraktionen" sx={{ mb: 1 }} />
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Promo Code Section (if used) */}
              {selectedBooking.promo_code && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ backgroundColor: '#e8f5e9' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        🎉 PROMO-CODE ANGEWENDET
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Code
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {selectedBooking.promo_code}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Rabatt
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                            - CHF {parseFloat(selectedBooking.discount_amount || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Ursprünglicher Preis
                          </Typography>
                          <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            CHF {parseFloat(selectedBooking.original_price || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </>
              )}

              {/* Price */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    GESAMTPREIS {selectedBooking.promo_code ? '(Nach Rabatt)' : ''}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    CHF {parseFloat(selectedBooking.total_price).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>

              {/* Payment Information */}
              {selectedBooking.payment_method && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      ZAHLUNGSINFORMATIONEN
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Zahlungsmethode
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {selectedBooking.payment_method === 'stripe' && '💳 Stripe'}
                              {selectedBooking.payment_method === 'twint' && '📱 TWINT'}
                              {selectedBooking.payment_method === 'direct' && '📄 Direkt (ohne Zahlung)'}
                              {!['stripe', 'twint', 'direct'].includes(selectedBooking.payment_method) && selectedBooking.payment_method}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Zahlungsstatus
                          </Typography>
                          <Box>
                            <Chip 
                              label={
                                selectedBooking.payment_status === 'paid' ? 'Bezahlt' :
                                selectedBooking.payment_status === 'pending' ? 'Ausstehend' :
                                selectedBooking.payment_status === 'failed' ? 'Fehlgeschlagen' :
                                selectedBooking.payment_status === 'refunded' ? 'Erstattet' :
                                selectedBooking.payment_status
                              }
                              color={
                                selectedBooking.payment_status === 'paid' ? 'success' :
                                selectedBooking.payment_status === 'pending' ? 'warning' :
                                selectedBooking.payment_status === 'failed' ? 'error' :
                                'default'
                              }
                              size="small"
                            />
                          </Box>
                        </Grid>
                        {selectedBooking.payment_intent_id && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Transaktions-ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {selectedBooking.payment_intent_id}
                            </Typography>
                          </Grid>
                        )}
                        {selectedBooking.payment_completed_at && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Bezahlt am
                            </Typography>
                            <Typography variant="body2">
                              {new Date(selectedBooking.payment_completed_at).toLocaleString('de-DE')}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBooking && (
            <>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handleViewTicket(selectedBooking.booking_id)}
                sx={{ mr: 1 }}
              >
                PDF Anzeigen
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadTicket(selectedBooking.booking_id)}
                sx={{ mr: 1 }}
              >
                PDF Herunterladen
              </Button>
            </>
          )}
          <Button onClick={() => setDetailsOpen(false)}>
            Schliessen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Buchung Bearbeiten</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event"
                select
                value={editFormData.event_type_id || ''}
                onChange={(e) => setEditFormData({ ...editFormData, event_type_id: e.target.value })}
              >
                {events.map((event) => (
                  <MenuItem key={event.id} value={event.id}>{event.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Status"
                select
                value={editFormData.status || ''}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <MenuItem value="confirmed">Bestätigt</MenuItem>
                <MenuItem value="pending">Ausstehend</MenuItem>
                <MenuItem value="cancelled">Storniert</MenuItem>
                <MenuItem value="completed">Abgeschlossen</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Datum"
                type="date"
                value={editFormData.booking_date || ''}
                onChange={(e) => setEditFormData({ ...editFormData, booking_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Uhrzeit"
                value={editFormData.time_slot || ''}
                onChange={(e) => setEditFormData({ ...editFormData, time_slot: e.target.value })}
                placeholder="HH:MM"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vorname"
                value={editFormData.first_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nachname"
                value={editFormData.last_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={editFormData.phone || ''}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </Grid>

            {isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Geburtstagskind
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Name des Geburtstagskindes"
                    value={editFormData.birthday_child_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday_child_name: e.target.value })}
                    error={!editFormData.birthday_child_name?.trim()}
                    helperText={!editFormData.birthday_child_name?.trim() ? 'Erforderlich' : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Geschlecht"
                    select
                    value={editFormData.birthday_child_gender || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday_child_gender: e.target.value })}
                    error={!editFormData.birthday_child_gender}
                    helperText={!editFormData.birthday_child_gender ? 'Erforderlich' : ' '}
                  >
                    <MenuItem value="junge">Junge</MenuItem>
                    <MenuItem value="maedchen">Mädchen</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Alert severity="info">
                Hinweis: Kinder und Extras werden über die Event-Konfiguration verwaltet. Für bestehende Buchungen können Sie hier nur die Basis-Informationen bearbeiten.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notizen"
                multiline
                rows={3}
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={
              saving ||
              (isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) &&
                (!editFormData.birthday_child_name?.trim() || !editFormData.birthday_child_gender))
            }
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neue Buchung Erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event"
                select
                value={editFormData.event_type_id || ''}
                onChange={(e) => {
                  const selectedEvent = events.find((event) => event.id === e.target.value);
                  // Only Halloween auto-fills the date here - deliberately
                  // not generalized to "any event with fixed_date", so
                  // toggling that field for some other event in the Events
                  // page never silently changes this dialog's date behavior
                  // and confuses staff/other admins booking normal events.
                  const isHalloween = selectedEvent && String(selectedEvent.event_key).toLowerCase() === 'halloween';
                  const halloweenEvent = events.find((event) => String(event.event_key).toLowerCase() === 'halloween');
                  // If switching AWAY from Halloween and the date field still
                  // holds exactly the date Halloween auto-filled (i.e. the
                  // admin hasn't manually changed it since), reset it back to
                  // today's default instead of leaving Halloween's date
                  // behind on whatever event gets picked next.
                  const isLeftoverHalloweenDate = !isHalloween && halloweenEvent?.fixed_date && editFormData.booking_date === halloweenEvent.fixed_date;
                  let newDate;
                  if (isHalloween && selectedEvent.fixed_date) {
                    newDate = selectedEvent.fixed_date;
                  } else if (isLeftoverHalloweenDate) {
                    newDate = getLocalDateString();
                  } else {
                    newDate = editFormData.booking_date;
                  }
                  setEditFormData({ ...editFormData, event_type_id: e.target.value, booking_date: newDate });
                  loadEventConfig(e.target.value, newDate, editFormData.time_slot);
                  if (newDate) {
                    loadTrampolineEventTimeSlots(newDate);
                  }
                }}
                required
              >
                {events.map((event) => (
                  <MenuItem key={event.id} value={event.id}>{event.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Status"
                select
                value={editFormData.status || 'confirmed'}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <MenuItem value="confirmed">Bestätigt</MenuItem>
                <MenuItem value="pending">Ausstehend</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Datum"
                type="date"
                value={editFormData.booking_date || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  // Validate: Check if booking is for today after 18:00
                  if (newDate) {
                    const selectedDate = new Date(newDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    selectedDate.setHours(0, 0, 0, 0);
                    
                    if (selectedDate.getTime() === today.getTime()) {
                      const currentHour = new Date().getHours();
                      if (currentHour >= 18) {
                        setError('Buchungen für heute sind nach 18:00 Uhr nicht mehr möglich. Bitte wählen Sie ein zukünftiges Datum.');
                        return;
                      }
                    }
                  }
                  
                  setEditFormData({ ...editFormData, booking_date: newDate });
                  if (editFormData.event_type_id) {
                    loadEventConfig(editFormData.event_type_id, newDate, editFormData.time_slot);
                  }
                  // Also reload Trampoline slots when date changes
                  if (newDate) {
                    loadTrampolineEventTimeSlots(newDate);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                helperText={(() => {
                  // Only warn when the SELECTED date is today - this used to
                  // fire for any date (including weeks out) just because the
                  // wall-clock time was past 18:00, which made every future
                  // booking look blocked.
                  if (!editFormData.booking_date) return '';
                  const selectedDate = new Date(editFormData.booking_date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  selectedDate.setHours(0, 0, 0, 0);
                  if (selectedDate.getTime() !== today.getTime()) return '';
                  const currentHour = new Date().getHours();
                  if (currentHour >= 18) {
                    return 'Buchungen für heute sind nach 18:00 Uhr nicht mehr möglich';
                  }
                  return '';
                })()}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Uhrzeit (optional)"
                select
                value={editFormData.time_slot || ''}
                onChange={(e) => setEditFormData({ ...editFormData, time_slot: e.target.value })}
                disabled={!eventConfig || loadingConfig}
                helperText={loadingConfig ? "Lade verfügbare Zeiten..." : eventConfig?.timeSlots?.length > 0 ? "Wählen Sie eine verfügbare Zeit (optional)" : "Bitte Event und Datum wählen"}
              >
                <MenuItem value="">Keine Zeit auswählen</MenuItem>
                {eventConfig?.timeSlots?.length > 0 && (
                  eventConfig.timeSlots.map((slot) => (
                    <MenuItem key={slot.time_slot} value={slot.time_slot}>
                      {slot.time_slot} {slot.remaining_capacity !== undefined ? `(${slot.remaining_capacity} frei)` : ''}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Kundendaten
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vorname"
                value={editFormData.first_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nachname"
                value={editFormData.last_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={editFormData.phone || ''}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                required
              />
            </Grid>

            {/* Birthday Child Info - only shown for birthday events */}
            {isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Geburtstagskind
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Name des Geburtstagskindes"
                    value={editFormData.birthday_child_name || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday_child_name: e.target.value })}
                    error={!editFormData.birthday_child_name?.trim()}
                    helperText={!editFormData.birthday_child_name?.trim() ? 'Erforderlich' : ' '}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Geschlecht"
                    select
                    value={editFormData.birthday_child_gender || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday_child_gender: e.target.value })}
                    error={!editFormData.birthday_child_gender}
                    helperText={!editFormData.birthday_child_gender ? 'Erforderlich' : ' '}
                  >
                    <MenuItem value="junge">Junge</MenuItem>
                    <MenuItem value="maedchen">Mädchen</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* Dynamic Child Categories */}
            {eventConfig?.childCategories && eventConfig.childCategories.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Kinder
                  </Typography>
                </Grid>
                {eventConfig.childCategories.map((category) => (
                  <Grid item xs={12} sm={6} key={category.id}>
                    <TextField
                      fullWidth
                      label={`${category.category_name} (CHF ${parseFloat(category.price).toFixed(2)})`}
                      type="number"
                      value={selectedChildren[category.id] || 0}
                      onChange={(e) => setSelectedChildren({ ...selectedChildren, [category.id]: parseInt(e.target.value) || 0 })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                ))}
              </>
            )}

            {/* Dynamic Extras */}
            {eventConfig?.extras && eventConfig.extras.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Extras
                  </Typography>
                </Grid>
                {eventConfig.extras.map((extra) => {
                  const isTrampolineExtra = extra.extra_name && (
                    extra.extra_name.toLowerCase().includes('trampoline') || 
                    extra.extra_name.toLowerCase().includes('trampolin')
                  );
                  
                  // Get time slots for this extra
                  let extraTimeSlots = [];
                  if (isTrampolineExtra && editFormData.booking_date) {
                    if (loadingTrampolineSlots) {
                      extraTimeSlots = [];
                    } else if (trampolineEventTimeSlots.length > 0) {
                      extraTimeSlots = trampolineEventTimeSlots
                        .filter(slot => slot.remaining_capacity > 0 && !slot.time_passed)
                        .map(slot => ({
                          time_slot: slot.time_slot,
                          remaining_capacity: slot.remaining_capacity
                        }));
                    }
                  } else {
                    extraTimeSlots = extra.timeSlots || extra.time_slots || [];
                    
                    // Filter by weekday availability for non-Trampoline extras
                    if (editFormData.booking_date && extraTimeSlots.length > 0) {
                      const bookingDate = new Date(editFormData.booking_date);
                      const dayOfWeek = bookingDate.getDay(); // 0=Sunday, 6=Saturday
                      const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to ISO (1=Monday, 7=Sunday)
                      const dayColumns = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      const dayName = dayColumns[isoDay - 1];
                      const availabilityKey = `available_${dayName}`;
                      
                      extraTimeSlots = extraTimeSlots.filter(slot => {
                        const isAvailable = slot[availabilityKey];
                        return isAvailable === 1 || 
                               isAvailable === true || 
                               isAvailable === "1" ||
                               isAvailable === undefined || 
                               isAvailable === null;
                      });
                    }
                  }
                  
                  const hasTimeSlots = Array.isArray(extraTimeSlots) && extraTimeSlots.length > 0;
                  const currentValue = selectedExtras[extra.id] || 0;
                  const selectedExtraTime = selectedExtraTimeSlots[extra.id] || '';
                  
                  
                  return (
                    <Grid item xs={12} key={extra.id}>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label={`${extra.extra_name} (CHF ${parseFloat(extra.price_per_child).toFixed(2)}${extra.price_type === 'per_child' ? ' / Kind' : ''})`}
                          type="number"
                          value={currentValue}
                          onChange={(e) => {
                            const newValue = parseInt(e.target.value) || 0;
                            setSelectedExtras({ ...selectedExtras, [extra.id]: newValue });
                            if (newValue === 0) {
                              const newTimeSlots = { ...selectedExtraTimeSlots };
                              delete newTimeSlots[extra.id];
                              setSelectedExtraTimeSlots(newTimeSlots);
                            }
                          }}
                          inputProps={{ min: 0 }}
                          sx={{ mb: hasTimeSlots && currentValue > 0 ? 2 : 0 }}
                        />
                        
                        {/* Time Slot Selector for Extra */}
                        {hasTimeSlots && currentValue > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                              Uhrzeit für {extra.extra_name}:
                            </Typography>
                            {isTrampolineExtra && loadingTrampolineSlots ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Lade verfügbare Zeitfenster...</Typography>
                              </Box>
                            ) : (
                              <Grid container spacing={1}>
                                {extraTimeSlots.length > 0 ? (
                                  extraTimeSlots.map((slot) => (
                                    <Grid item xs={6} sm={4} key={slot.time_slot}>
                                      <Button
                                        fullWidth
                                        variant={selectedExtraTime === slot.time_slot ? 'contained' : 'outlined'}
                                        onClick={() => {
                                          const newTimeSlots = { ...selectedExtraTimeSlots, [extra.id]: slot.time_slot };
                                          setSelectedExtraTimeSlots(newTimeSlots);
                                        }}
                                        size="small"
                                        sx={{
                                          fontSize: '0.85rem',
                                          py: 0.5,
                                          minHeight: 36,
                                        }}
                                      >
                                        {slot.time_slot}
                                        {slot.remaining_capacity !== undefined && (
                                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.8 }}>
                                            {slot.remaining_capacity} Plätze
                                          </Typography>
                                        )}
                                      </Button>
                                    </Grid>
                                  ))
                                ) : (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Keine verfügbaren Zeitfenster
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Gesamtpreis: CHF {calculateDynamicPrice()}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notizen"
                multiline
                rows={3}
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleCreateBooking} 
            variant="contained" 
            disabled={
              saving ||
              !editFormData.event_type_id ||
              !editFormData.booking_date ||
              (isBirthdayEventEntry(events.find((e) => e.id === editFormData.event_type_id)) &&
                (!editFormData.birthday_child_name?.trim() || !editFormData.birthday_child_gender))
            }
          >
            {saving ? 'Erstellen...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Buchung Löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Buchung <strong>{selectedBooking?.booking_id}</strong> wirklich löschen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Diese Aktion kann nicht rückgängig gemacht werden!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={saving}
          >
            {saving ? 'Löschen...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bookings;
